import { Button, CloseButton } from '@ui/shared/Button';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import styled from 'styled-components';
import {
  closeInnerPopup,
  openInnerPopup,
} from '../../../data/providers/popupsProvider';
import { scoreboardSettingsProvider } from '../../../data/providers/settings/scoreboardSettings';
import {
  IScoreboardData,
  scoreboardTestsProvider,
} from '../../../data/providers/testsProvider';
import {
  IScoreboardSettings,
  ISelectableMetric,
  ISelectableTag,
  ISelectableTestType,
  ISelectOption,
  ITest,
} from '../../../data/types';
import { clearScoreboardSettings } from '../../../data/writers/settings/scoreboardSettings';
import { downloadScoreboard } from '../../../utils/export';
import { queryHashGenerator } from '../../../utils/queryHash';
import { Column } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { CardBody } from '../../shared/card/CardBody';
import { CardFooter } from '../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { InvalidSettings } from '../../shared/InvalidSettings';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { PopupCard, PopupCardContent } from '../../shared/popups/PopupCard';
import { Test } from '../test/Test';
import { LatestTest } from './LatestTest';
import { ScoreboardList } from './ScoreboardList';
import { ScoreboardSettings } from './ScoreboardSettings';

interface IProps extends RouteComponentProps {
  onClose: () => void;
}
/**
 *
 *
 * @param {IProps} props
 * @returns {React.ReactElement}
 */
export const Scoreboard = withRouter((props: IProps): React.ReactElement => {
  const [tests, setTests] = React.useState<ITest[]>([]);
  const [settings, setSettings] = React.useState<IScoreboardSettings>();
  const [invalidSettings, setInvalidSettings] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(true);

  /**
   * @description the currently selected metric
   */
  const currentMetric = React.useMemo((): ISelectableMetric => {
    return settings?.metrics[settings?.testType?.value];
  }, [
    JSON.stringify(settings?.metrics),
    JSON.stringify(settings?.testType?.value),
  ]);

  /**
   * @description the currently selected test type
   */
  const currentTestType = React.useMemo((): ISelectableTestType => {
    return settings?.testType;
  }, [settings?.testType?.value]);

  /**
   * @description downloads the current scoreboard
   * @param mData
   * @param metricObj
   * @param name
   */
  const saveScoreboardData = (
    mData: ITest[],
    metricObj: ISelectOption,
    name: string
  ): void => {
    downloadScoreboard(mData, metricObj, name);
  };

  /**
   * @description subscribes to scoreboard settings and the tests for the scoreboard
   */
  React.useEffect((): (() => void) => {
    setLoading(true);
    const testsSubscriber = scoreboardSettingsProvider()
      /// subscribe to tests that match the settings
      .pipe(
        switchMap(
          (mSettings: IScoreboardSettings): Observable<IScoreboardData> => {
            setLoading(true);
            return scoreboardTestsProvider(mSettings);
          }
        ),
        catchError((err, input) => {
          setInvalidSettings(true);
          setLoading(false);
          return input;
        })
      )
      .subscribe((scoreboardData: IScoreboardData): void => {
        setTests(scoreboardData.data);
        setSettings(scoreboardData.settings);
        setLoading(false);
        setInvalidSettings(false);
      });

    return (): void => {
      testsSubscriber.unsubscribe();
    };
  }, []);

  /**
   * @description determines if a test matches any applied filters
   * @param test
   * @returns
   */
  const testMatchesFilters = (test: ITest): boolean => {
    if (!test) return false;
    if (
      settings?.filteredGroups?.length > 0 ||
      settings?.filteredTeams?.length > 0
    ) {
      if (
        settings?.filteredGroups?.length > 0 &&
        test?.groups.some((gId: string): boolean =>
          settings?.filteredGroups?.includes(gId)
        )
      ) {
        return true;
      }
      if (
        settings?.filteredTeams?.length > 0 &&
        test.teams.some((tId: string): boolean =>
          settings?.filteredTeams.includes(tId)
        )
      ) {
        return true;
      }
      return false;
    } else {
      return true;
    }
  };

  /**
   * @description memoized list of the scoreboard data
   */
  const data = React.useMemo((): { list: ITest[]; latest: ITest } => {
    let testsThatMatch = [];
    let sortedData = [];
    let scoreboardList = [];
    if (isEmpty(currentMetric) || isEmpty(settings?.testType)) {
      return {
        list: [],
        latest: null,
      };
    }

    const queryHash = queryHashGenerator(
      settings?.testType.value,
      settings?.tags.map(
        (selectableTag: ISelectableTag): string => selectableTag.value
      )
    );

    if (queryHash) {
      testsThatMatch = tests.reduce((acc: ITest[], item: ITest): ITest[] => {
        if (testMatchesFilters(item)) {
          if (item[currentMetric.value] && item.queryHash === queryHash) {
            acc.push(item);
          }
        }
        return acc;
      }, []);

      sortedData = sortBy(testsThatMatch, (item: ITest): number => {
        const direction = item[currentMetric.value].direction;

        if (direction === 1) {
          /// higher number is better
          return -item[currentMetric.value].value;
        } else if (direction === -1) {
          /// lower number is better
          return item[currentMetric.value].value;
        } else {
          /// closer to 0 is better
          return Math.abs(0 - Math.abs(item[currentMetric.value].value));
        }
      });

      scoreboardList = uniqBy(
        sortedData,
        (item: ITest): string => item.athlete.id
      );
    }

    if (scoreboardList.length === 0) {
      return {
        list: [],
        latest: null,
      };
    }

    const latest = testsThatMatch?.[0];

    /// only show the top 100
    return {
      list: scoreboardList.slice(0, 100),
      latest,
    };
  }, [
    tests.length,
    currentTestType?.value,
    JSON.stringify(settings?.filteredTeams),
    JSON.stringify(settings?.filteredGroups),
    settings?.tags?.length,
    currentMetric,
  ]);

  /**
   * @description handles the save scoreboard data action
   */
  const handleSaveScoreboard = (): void => {
    saveScoreboardData(
      data.list,
      currentMetric,
      `${currentTestType.label}_${currentMetric.label}`
    );
  };

  /**
   * @description handles the open test action
   * @param testId
   */
  const goToTest = (test: ITest): void => {
    openInnerPopup(
      <Test
        test={test}
        onClose={closeInnerPopup(props.history)}
        testList={data.list}
        level={2}
      />,
      props.history,
      `test-${test.id}`
    );
  };

  return (
    <PopupCard onClose={props.onClose} size='full' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <InfoContainer>
              <Title>Scoreboard</Title>
            </InfoContainer>
          </CardHeaderLeft>
          <CardHeaderRight style={{ flex: 0, flexDirection: 'column' }}>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody style={{ backgroundColor: 'var(--color-surface-0)' }}>
          <Column style={{ width: '100%', height: '100%' }}>
            {invalidSettings ? (
              <InvalidSettings
                message='Invalid Settings'
                attemptFix={clearScoreboardSettings}
              />
            ) : !loading ? (
              <Row style={{ alignItems: 'flex-start' }} shouldCollapse>
                <LatestTest
                  test={data.latest}
                  metric={settings?.metrics[settings?.testType.value]}
                  testType={settings?.testType}
                />
                <ScoreboardList
                  data={data.list}
                  selectTest={goToTest}
                  settings={settings}
                />
              </Row>
            ) : (
              <LinearLoading />
            )}
          </Column>
        </CardBody>
        <CardFooter surface={8} style={{ justifyContent: 'center' }}>
          <ScoreboardSettings settings={settings} disabled={loading} />
          <Button
            disabled={data.list?.length === 0}
            onClick={handleSaveScoreboard}
            icon='cloud_download'
            title='Download'
            style={{ whiteSpace: 'nowrap' }}
          />
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
});

const Title = styled.h5`
  padding-right: 10px;
`;

const TagContainer = styled.div`
  flex-wrap: wrap;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
`;

const NoResultsState = styled.div`
  flex: 1;
  height: 100%;
  align-items: center;
  justify-content: center;
  display: flex;
  flex-direction: column;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0px 10px;
  align-items: center;
  @media (max-width: 990px) {
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
  }
`;

const SecondaryContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
const SecondaryText = styled.div`
  margin-left: 10px;
  color: var(--color-secondary-text);
  @media (max-width: 990px) {
    margin-left: 0px;
  }
`;
